import React, { useEffect, useState } from "react";
import _ from 'lodash';
import { Button } from "../Button";
import ReactSelect from 'react-select';
import Section from "./Section";
import './budget.css';
import { CalcRow, CalcSection, calculateCategory, calculateRowTotal, capitalize, getBudgetPeriodLabel, getFrequencyLabel, periodOptions, sections } from './helpers';
import { parseFullAmount, parseSmallAmount, postHeightToParent } from "../common-helpers";

const initialState = {
	selectedBudgetPeriod: 12,
	income: sections.income,
	expenses: sections.expenses,
};

export const Budget: React.FC = () => {
	const [ calcValues, setCalcValues ] = useState(_.cloneDeep(initialState));

	const [incomeTotalsList, setIncomeTotalsList] = useState(calculateCategory(calcValues.income, calcValues.selectedBudgetPeriod));//row total values array
	const [expensesTotalsList, setExpensesTotalsList] = useState(calculateCategory(calcValues.expenses, calcValues.selectedBudgetPeriod));

	const [ incomeWorth, setIncomeWorth ] = useState(incomeTotalsList.reduce((a, b) => a + b, 0));
	const [ expensesWorth, setExpensesWorth ] = useState(expensesTotalsList.reduce((a, b) => a + b, 0));

	const countRows = (rows: CalcSection[]) => {
		return rows.reduce((prev: number, curr: CalcSection) => prev + curr.data.length, 0);
	};

	const [ rowCount, setRowCount ] = useState(countRows(initialState.income) + countRows(initialState.expenses));

	const calculateSectionTotal = (section: CalcSection) => {
		let sectionValueArray: number[] = [];
		section.data.map(r => sectionValueArray.push(calculateRowTotal(r, calcValues.selectedBudgetPeriod)));
		return sectionValueArray.reduce((a, b) => a + b, 0);
	};

	const updateIncomeTotals = (newList: number[]) => {
		let updatedWorth = newList.reduce((a, b) => a + b, 0);
		setIncomeTotalsList(newList);
		setIncomeWorth(updatedWorth);
	};

	const updateExpensesTotals = (newList: number[]) => {
		let updatedWorth = newList.reduce((a, b) => a + b, 0);
		setExpensesTotalsList(newList);
		setExpensesWorth(updatedWorth);
	};

	const addRow = (category: string, sectionIndex: number) => {
		let categorySections = calcValues[category];
		let updatedSections = categorySections.map((el: CalcSection, i: number) => {
			if (i === sectionIndex) { el.data.push({ label: capitalize(category) + ' ' + (el.data.length + 1), value: '$0', frequency: 12 }); }
			return el;
		});

		if (category === 'income') {
			setCalcValues((values => ({...values, income: updatedSections})));
			setIncomeTotalsList(calculateCategory(updatedSections, calcValues.selectedBudgetPeriod));
			setRowCount(countRows(updatedSections) + countRows(calcValues.expenses));
		} else {
			setCalcValues((values => ({...values, expenses: updatedSections})));
			setExpensesTotalsList(calculateCategory(updatedSections, calcValues.selectedBudgetPeriod));
			setRowCount(countRows(calcValues.income) + countRows(updatedSections));
		}
	};

	const updateRow = (category: string, sectionIndex: number, rowIndex: number, row: CalcRow) => {
		let categorySections = calcValues[category];
		let updatedSections = categorySections.map((el: CalcSection, i: number) => {
			if (i === sectionIndex) {
				el.data[rowIndex].label = row.label;
				el.data[rowIndex].value = row.value;
				el.data[rowIndex].frequency = row.frequency;
			}
			return el;
		});

		if (category === 'income') {
			setCalcValues((values => ({...values, income: updatedSections})));
			updateIncomeTotals(calculateCategory(updatedSections, calcValues.selectedBudgetPeriod));
		} else {
			setCalcValues((values => ({...values, expenses: updatedSections})));
			updateExpensesTotals(calculateCategory(updatedSections, calcValues.selectedBudgetPeriod) );
		}
	};

	const deleteRow = (category: string, sectionIndex: number, rowIndex: number) => {
		let categorySections = calcValues[category];
		let updatedSections = categorySections.map((el: CalcSection, i: number) => {
			if (i === sectionIndex) {
				let newDataArray = el.data.filter((row, i) => i !== rowIndex );
				el.data = newDataArray;
			}
			return el;
		});

		if (category === 'income') {
			setCalcValues((values => ({...values, income: updatedSections})));
			let newIncomeTotalsList = calculateCategory(updatedSections, calcValues.selectedBudgetPeriod);
			setIncomeTotalsList(newIncomeTotalsList);
			setIncomeWorth(newIncomeTotalsList.reduce((a, b) => a + b, 0));
			setRowCount(countRows(updatedSections) + countRows(calcValues.expenses));
		} else {
			setCalcValues((values => ({...values, expenses: updatedSections})));
			let newExpensesTotalsList = calculateCategory(updatedSections, calcValues.selectedBudgetPeriod);
			setExpensesTotalsList(newExpensesTotalsList);
			setExpensesWorth(newExpensesTotalsList.reduce((a, b) => a + b, 0));
			setRowCount(countRows(calcValues.income) + countRows(updatedSections));
		}
	};

	const createCategoryCSVstring = (sections: CalcSection[]): string => {
		let csv: string = '';
		sections.forEach(el => {
			csv += el.sectionLabel + ",,,\n";
			csv += "Expense,Amount,Frequency,Amount for Budget Period\n\n";
			el.data.forEach(row => {
				let rowValue = row.value.replaceAll(",","");
				let rowFrequency = getFrequencyLabel(row.frequency);
				let rowTotal = parseSmallAmount(calculateRowTotal(row, calcValues.selectedBudgetPeriod).toString()).replaceAll(",","");
				csv += row.label + ',' + rowValue + ',' + rowFrequency + ',' + rowTotal + '\n';
			});
			csv += "Budget Period Total,,," + parseSmallAmount(calculateSectionTotal(el).toString()).replaceAll(",","");//TODO:create custom parser
			csv += "\n\n";
		});
		return csv;
	};

	const downloadCSV = () => {
		let csv = "Household Budget Worksheet\n\n"
		csv += "Budget Period " + getBudgetPeriodLabel(calcValues.selectedBudgetPeriod) + "\n\n";
		csv += createCategoryCSVstring(calcValues.income);
		csv += createCategoryCSVstring(calcValues.expenses);
		csv += 'Total Income,,,' + parseSmallAmount(incomeWorth.toString()).replaceAll(",","") + "\n";
		csv += 'Total Expenses,,,' + parseSmallAmount(expensesWorth.toString()).replaceAll(",","") + "\n";
		csv += 'Income Minus Expenses,,,' + parseFullAmount((incomeWorth - expensesWorth)).slice(0, -3).replaceAll(",","");

    // document.write(csv);
		let hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
    hiddenElement.target = '_blank';
    hiddenElement.download = 'household-budget.csv';//provides the name for the CSV file to be downloaded
    hiddenElement.click();
	};

	const onReset = () => {
		let initialValues = _.cloneDeep(initialState);
		let initialIncomeTotals = calculateCategory(initialValues.income, initialValues.selectedBudgetPeriod);
		let initialExpensesTotals = calculateCategory(initialValues.expenses, initialValues.selectedBudgetPeriod);
		setCalcValues(initialValues);
		setIncomeTotalsList(calculateCategory(initialValues.income, initialState.selectedBudgetPeriod));
		setExpensesTotalsList(calculateCategory(initialValues.expenses, initialState.selectedBudgetPeriod));
		setIncomeWorth(initialIncomeTotals.reduce((a, b) => a + b, 0));
		setExpensesWorth(initialExpensesTotals.reduce((a, b) => a + b, 0));
		setRowCount(countRows(initialValues.income) + countRows(initialValues.expenses));
	};

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [rowCount]);

	return (
		<div className="budget-worksheet">
			<form>
				<label className="budget-period-label"><span className="budget-period-label-text">Budget Period</span>
				<ReactSelect
					instanceId="period"
					name="period"
					options={periodOptions}
					defaultValue={[{label: "12 Months", value: 12}]}
					value={periodOptions.filter(({ value }) => value === calcValues.selectedBudgetPeriod)}
					onChange={(newValue, actionMeta) => {
						let newBudgetPeriod = newValue ? newValue.value : 12;
						let newIncomeList = calculateCategory(calcValues.income, newBudgetPeriod);
						let newExpensesList = calculateCategory(calcValues.expenses, newBudgetPeriod);
						setCalcValues(values => ({...values, selectedBudgetPeriod: newBudgetPeriod}));
						setIncomeTotalsList(newIncomeList);
						setIncomeWorth(newIncomeList.reduce((a, b) => a + b, 0));
						setExpensesTotalsList(newExpensesList);
						setExpensesWorth(newExpensesList.reduce((a, b) => a + b, 0));
					}}
					isSearchable={true}
					className="budget-period-select"
					placeholder="Select budget period"
				/>
				</label>
				{ calcValues.income && calcValues.income.map((el, i) => (
					<Section
						category='income'
						section={el}
						sectionIndex={i}
						sectionTotal={calculateSectionTotal(el)}
						selectedBudgetPeriod={calcValues.selectedBudgetPeriod}
						addRow={addRow}
						updateState={updateRow}
						deleteRow={deleteRow}
						key={`section-income-${i}`}
					/>
				))}
				{ calcValues.expenses && calcValues.expenses.map((el, i) => (
					<Section
						category='expenses'
						section={el}
						sectionIndex={i}
						sectionTotal={calculateSectionTotal(el)}
						selectedBudgetPeriod={calcValues.selectedBudgetPeriod}
						addRow={addRow}
						updateState={updateRow}
						deleteRow={deleteRow}
						key={`section-exp-${i}`}
					/>
				))}
				<div className="budget-totals"><span>Total Income</span><span>{parseSmallAmount(incomeWorth.toString())}</span></div>
				<div className="budget-totals"><span>Total Expenses</span><span>{parseSmallAmount(expensesWorth.toString())}</span></div>
				<div className="budget-totals"><span>Income Minus Expenses</span><span>{parseFullAmount((incomeWorth - expensesWorth)).slice(0, -3)}</span></div>
				<Button
					label="Download"
					className="primary-button budget-btn"
					onClickHandler={downloadCSV}
				/>
				<Button
					label="Clear"
					className="default-button budget-btn"
					onClickHandler={onReset}
				/>
			</form>
		</div>
	);
};
